/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useCardPaymentFlow` for the Caintra Connect frontend.
 * @module hooks/useCardPaymentFlow
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useCallback } from "react";
import { createCardCharge, getCardChargeStatus } from "../logic/openpay/index.js";
import {
  initializeOpenPayClient,
  waitForOpenPaySDK,
} from "../utils/openpayClient.js";
import {
  buildOpenPayCardData,
  validateCardFormFields,
} from "../utils/payment/cardValidation.js";
import { tokenizeCard } from "../utils/payment/openpayTokenization.js";
import {
  closePopupIfOpen,
  openThreeDSPopup,
  waitForThreeDSCompletion,
} from "../utils/payment/threeDSPopup.js";
import {
  DEFAULT_MAX_POLLING_ATTEMPTS,
  PAYMENT_STATUS_ACTIONS,
  resolvePaymentStatusAction,
} from "../utils/payment/paymentStatusReducer.js";
import { logger, sanitizeError } from "../utils/logger.js";

const POP_BLOCKED_MESSAGE =
  "Popup blocked. Please allow popups for this site and try again.";

/**
 * Shared card payment + 3DS polling flow used by payment modals.
 * @param {Object} params
 * @returns {{ openPopupWindow: Function, verifyPaymentStatus: Function, handleCardPayment: Function }}
 */
export const useCardPaymentFlow = ({
  resultStepNumber,
  stepNumber,
  setStepNumber,
  setPaymentStatus,
  setCardPaymentLoading,
  setModalMessage,
  setPopupWindow,
  setPollingAttempts,
  shouldContinuePolling,
  pollingTimeoutRef,
  navigate,
  t,
  user,
  formData,
  validateBeforePayment,
  buildCardChargeData,
}) => {
  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, [pollingTimeoutRef]);

  const finalizePaymentFlow = useCallback(
    (status) => {
      setPollingAttempts(0);
      setModalMessage(null);
      setPaymentStatus(status);
      setCardPaymentLoading(false);
      clearPollingTimeout();
      if (stepNumber !== resultStepNumber) {
        setStepNumber(stepNumber + 1);
      }
    },
    [
      clearPollingTimeout,
      resultStepNumber,
      setCardPaymentLoading,
      setModalMessage,
      setPaymentStatus,
      setPollingAttempts,
      setStepNumber,
      stepNumber,
    ],
  );

  const openPopupWindow = useCallback(() => {
    const popup = openThreeDSPopup({
      preparingText: t("components.paymentModal.threeDSPreparing"),
      processingText: t("components.paymentModal.threeDSProcessing"),
    });

    if (!popup) {
      return null;
    }

    setPopupWindow(popup);
    return popup;
  }, [setPopupWindow, t]);

  const verifyPaymentStatus = useCallback(
    async (chargeId, attemptNumber = 0, popupWasClosed = false) => {
      if (!shouldContinuePolling.current) {
        logger.info("Polling stopped - modal closed or user navigated away");
        return;
      }

      try {
        setModalMessage(t("components.paymentModal.threeDSVerifying"));

        const chargeStatus = await getCardChargeStatus(chargeId, navigate);

        if (!shouldContinuePolling.current) {
          logger.info("Polling stopped - modal closed or user navigated away");
          return;
        }

        if (!chargeStatus) {
          throw new Error(t("components.paymentModal.threeDSVerifyFailed"));
        }

        const { action, nextAttempt } = resolvePaymentStatusAction(
          chargeStatus.status,
          {
            attemptNumber,
            popupWasClosed,
            maxAttempts: DEFAULT_MAX_POLLING_ATTEMPTS,
          },
        );

        if (action === PAYMENT_STATUS_ACTIONS.SUCCESS) {
          finalizePaymentFlow("PAYMENT_SUCCESS");
          return;
        }

        if (action === PAYMENT_STATUS_ACTIONS.ERROR) {
          if (
            popupWasClosed &&
            (chargeStatus.status === "charge_pending" ||
              chargeStatus.status === "in_progress")
          ) {
            logger.info(
              "Popup was closed and payment is still pending - authentication was not completed",
            );
          } else if (attemptNumber >= DEFAULT_MAX_POLLING_ATTEMPTS) {
            logger.error("Maximum polling attempts reached");
          } else {
            // Transient error before max attempts; finalize below without extra logging.
          }
          finalizePaymentFlow("PAYMENT_ERROR");
          return;
        }

        pollingTimeoutRef.current = setTimeout(() => {
          if (shouldContinuePolling.current) {
            verifyPaymentStatus(chargeId, nextAttempt, popupWasClosed);
          }
        }, 2000);
      } catch (error) {
        logger.error("Error verifying payment status:", {
          err: sanitizeError(error),
        });
        finalizePaymentFlow("PAYMENT_ERROR");
      }
    },
    [
      finalizePaymentFlow,
      navigate,
      pollingTimeoutRef,
      setModalMessage,
      shouldContinuePolling,
      t,
    ],
  );

  const navigatePopupTo3DS = useCallback(
    async (popup, chargeId, authenticationUrl) => {
      await waitForThreeDSCompletion({
        popup,
        authenticationUrl,
        origin: window.location.origin,
        onAuthenticating: () => {
          setModalMessage(t("components.paymentModal.threeDSAuthenticating"));
        },
        onComplete: (popupWasClosed) => {
          verifyPaymentStatus(chargeId, 0, popupWasClosed);
        },
        timeoutMessage: t("components.paymentModal.threeDSTimeout"),
      });
    },
    [setModalMessage, t, verifyPaymentStatus],
  );

  const handleCardPayment = useCallback(async () => {
    if (!validateBeforePayment()) {
      return;
    }

    setCardPaymentLoading(true);

    if (stepNumber !== resultStepNumber) {
      setPaymentStatus(null);
    }

    const popup = openPopupWindow();
    if (!popup) {
      setCardPaymentLoading(false);
      setPaymentStatus("PAYMENT_ERROR");
      setModalMessage(POP_BLOCKED_MESSAGE);
      if (stepNumber !== resultStepNumber) {
        setStepNumber(stepNumber + 1);
      }
      return;
    }

    try {
      await waitForOpenPaySDK();

      const OpenPay = initializeOpenPayClient();
      const deviceSessionId = OpenPay.deviceData.setup();

      logger.info("Device Session ID: ", { data: deviceSessionId });

      validateCardFormFields(formData);
      const { cardData, cardNumber } = buildOpenPayCardData(
        formData,
        deviceSessionId,
      );

      logger.info("Card Data: ", { data: cardData });

      const tokenResponse = await tokenizeCard(OpenPay, cardData);

      logger.info("Token Response: ", { data: tokenResponse });

      const cardChargeData = buildCardChargeData({
        tokenResponse,
        deviceSessionId,
        cardNumber,
      });

      logger.info("Card Charge Data: ", { data: cardChargeData });

      const data = await createCardCharge(user._id, cardChargeData, navigate);

      if (!data || !data.cardChargeCreated) {
        throw new Error("Invalid response from payment gateway");
      }

      const charge = data.cardChargeCreated;

      if (charge.requires3DSecure && charge.authenticationUrl) {
        logger.info("3DS authentication required, navigating popup...");
        try {
          await navigatePopupTo3DS(popup, charge.id, charge.authenticationUrl);
        } catch (error) {
          logger.error("Error during 3DS authentication:", {
            err: sanitizeError(error),
          });
          closePopupIfOpen(popup);
          setPopupWindow(null);
          finalizePaymentFlow("PAYMENT_ERROR");
        }
        return;
      }

      closePopupIfOpen(popup);
      setPopupWindow(null);

      if (charge.status === "completed") {
        logger.info("Card charge created successfully:", { data: data });
        finalizePaymentFlow("PAYMENT_SUCCESS");
        return;
      }

      logger.error("Unexpected response from payment gateway:", {
        data: data,
      });
      finalizePaymentFlow("PAYMENT_ERROR");
    } catch (error) {
      logger.error("Error processing card payment:", {
        err: sanitizeError(error),
      });
      closePopupIfOpen(popup);
      setPopupWindow(null);
      finalizePaymentFlow("PAYMENT_ERROR");
    }
  }, [
    buildCardChargeData,
    finalizePaymentFlow,
    formData,
    navigate,
    navigatePopupTo3DS,
    openPopupWindow,
    resultStepNumber,
    setCardPaymentLoading,
    setModalMessage,
    setPaymentStatus,
    setPopupWindow,
    setStepNumber,
    stepNumber,
    user._id,
    validateBeforePayment,
  ]);

  return {
    openPopupWindow,
    verifyPaymentStatus,
    handleCardPayment,
  };
};
